import * as stylex from '@stylexjs/stylex';
import { Trans, useTranslation } from 'react-i18next';
import { LoadingSpinner } from '@/main/LoadingSpinner.molecule';
import { formatNumberForLocale } from '@/utilities/numberHelper.utilities';
import { faChevronDown } from '@seeqdev/icons/fa-sharp-regular-svg-icons';
import { Badge, ButtonWithDropdown } from '@seeqdev/qomponents';
import type { BadgeVariant } from '@seeqdev/qomponents';

/** Opens the list of approval steps when the status badge is clicked. */
const ViewStepsBadge = ({
  variant,
  label,
  testId,
  onClick,
}: {
  variant: BadgeVariant;
  label: string;
  testId: string;
  onClick: () => void;
}) => (
  <button
    type="button"
    aria-haspopup="dialog"
    className="tw:flex tw:cursor-pointer tw:border-0 tw:bg-transparent tw:p-0 tw:hover:underline"
    data-testid={testId}
    onClick={onClick}>
    <Badge variant={variant}>{label}</Badge>
  </button>
);

/** Shows approval progress and the available step actions. */
export const ApprovalFlowHeaderDisplay = ({
  isInProd,
  approved,
  isResolvingProductionStatus,
  pendingStepName,
  currentStepNumber,
  totalSteps,
  canApprove,
  isDialogOpen,
  onApprove,
  onViewSteps,
}: {
  isInProd: boolean;
  approved: boolean | undefined;
  isResolvingProductionStatus: boolean;
  pendingStepName: string | undefined;
  currentStepNumber?: number;
  totalSteps?: number;
  canApprove: boolean;
  isDialogOpen: boolean;
  onApprove: () => void;
  onViewSteps: () => void;
}) => {
  const { t } = useTranslation();

  if (isInProd) {
    return (
      <ViewStepsBadge
        variant="success"
        label={t('APPROVAL_FLOWS.IN_PROD')}
        testId="approvalFlowInProductionBadge"
        onClick={onViewSteps}
      />
    );
  }
  if (approved) {
    if (isResolvingProductionStatus) {
      return <LoadingSpinner />;
    }
    return (
      <ViewStepsBadge
        variant="neutral"
        label={t('APPROVAL_FLOWS.DEPRECATED')}
        testId="approvalFlowDeprecatedBadge"
        onClick={onViewSteps}
      />
    );
  }
  return (
    <PendingApprovalPill
      name={pendingStepName ?? ''}
      currentStepNumber={currentStepNumber}
      totalSteps={totalSteps}
      canApprove={canApprove}
      isDialogOpen={isDialogOpen}
      onApprove={onApprove}
      onViewSteps={onViewSteps}
    />
  );
};

/** Fits the translated step label to its header slot without truncating the step position. */
const PendingApprovalPill = ({
  name,
  currentStepNumber,
  totalSteps,
  canApprove,
  isDialogOpen,
  onApprove,
  onViewSteps,
}: {
  name: string;
  currentStepNumber?: number;
  totalSteps?: number;
  canApprove: boolean;
  isDialogOpen: boolean;
  onApprove: () => void;
  onViewSteps: () => void;
}) => {
  const { t, i18n } = useTranslation();
  const hasPosition = currentStepNumber !== undefined && totalSteps !== undefined && totalSteps > 0;
  const current = hasPosition ? formatNumberForLocale(currentStepNumber, i18n.language) : undefined;
  const total = hasPosition ? formatNumberForLocale(totalSteps, i18n.language) : undefined;
  const label = hasPosition ? t('APPROVAL_FLOWS.HEADER_STEP', { current, total, name }) : name;
  return (
    <ButtonWithDropdown
      label={
        <span {...stylex.props(styles.label)}>
          {hasPosition ? (
            <Trans
              i18nKey="APPROVAL_FLOWS.HEADER_STEP"
              values={{ current, total, name: '<name />' }}
              components={{ name: <ApprovalStepName key="name" name={name} /> }}
            />
          ) : (
            <span {...stylex.props(styles.name)}>{name}</span>
          )}
        </span>
      }
      icon={faChevronDown}
      iconPosition="right"
      variant="outline"
      xstyle={styles.trigger}
      aria-label={label}
      tooltip={label}
      size="xs"
      testId="approvalFlowStep"
      setFocusOnTriggerOnClose={!isDialogOpen}
      dropdownItems={[
        {
          label: t('APPROVAL_FLOWS.OPTIONS.APPROVE_STEP'),
          // Approving a step requires write access to that step. The API checks this too.
          disabled: !canApprove,
          onClick: onApprove,
        },
        {
          label: t('APPROVAL_FLOWS.OPTIONS.VIEW_STEPS'),
          onClick: onViewSteps,
        },
      ]}
    />
  );
};

/** Keeps the name as React text, including names that contain translation markup characters. */
const ApprovalStepName = ({ name }: { name: string }) => <span {...stylex.props(styles.name)}>{name}</span>;

const styles = stylex.create({
  trigger: {
    minWidth: 0,
    maxWidth: '100%',
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1fr) auto',
    alignItems: 'center',
  },
  // Grid, not flex: with flex the position text overflows onto the chevron.
  label: {
    display: 'grid',
    gridAutoFlow: 'column',
    minWidth: 0,
    maxWidth: 280,
    overflow: 'hidden',
    whiteSpace: 'pre',
    textAlign: 'start',
  },
  name: { minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
});
